import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useModuleConfig } from '../hooks/useModuleConfig';
import { useLearnerState } from '../hooks/useLearnerState';
import { ModuleShell } from '../components/layout/ModuleShell';
import { SlideViewer } from '../components/slides/SlideViewer';
import { AssessmentPage } from './AssessmentPage';
import { ReflectionPage } from './ReflectionPage';
import { FinalReflection } from '../components/reflection/FinalReflection';
import { VoiceReflection } from '../components/voice/VoiceReflection';
import { exportLearnerData } from '@/lib/api';
import type { AgentConfig } from '@/lib/types';

export function ModulePage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { config, loading: configLoading, error: configError } = useModuleConfig(moduleId!);
  const { state, loading: stateLoading, resume, initialize } = useLearnerState();
  const [reflectionAgent, setReflectionAgent] = useState<AgentConfig | null>(null);
  const [moduleComplete, setModuleComplete] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Try to resume an existing session first; fall back to creating a new one
  useEffect(() => {
    if (!moduleId || state) return;

    const educatorName = localStorage.getItem('itari_educator_name') || undefined;

    const boot = async () => {
      const resumed = await resume(moduleId, educatorName);
      if (!resumed) {
        await initialize(moduleId, educatorName);
      }
    };
    boot();
  }, [moduleId, state, resume, initialize]);

  // Load reflection agent config when in reflection phase
  useEffect(() => {
    const loadReflectionAgent = async () => {
      if (state?.currentPosition.phase === 'reflection' && config) {
        const currentPartIndex = state.currentPosition.partIndex;
        const partId = `part-${currentPartIndex + 1}`;

        try {
          const response = await fetch(`/config/modules/${moduleId}/parts/${partId}/agents.yaml`);
          if (!response.ok) {
            throw new Error(`Failed to load agents.yaml (${response.status})`);
          }
          const yamlText = await response.text();
          const yaml = await import('yaml');
          const agents = yaml.parse(yamlText);
          setReflectionAgent(agents.reflection);
        } catch (error) {
          console.error('Failed to load reflection agent:', error);
        }
      }
    };

    loadReflectionAgent();
  }, [state?.currentPosition.phase, state?.currentPosition.partIndex, moduleId, config]);

  const handleExport = async () => {
    if (!state) return;
    setIsExporting(true);
    try {
      await exportLearnerData(state.id);
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (configLoading || stateLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading module...</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-serif font-semibold mb-4 text-red-600">Error Loading Module</h2>
          <p className="text-slate-600 dark:text-slate-400">{configError.message}</p>
        </div>
      </div>
    );
  }

  if (!config || !state) {
    return null;
  }

  // ── Module completion screen ──
  if (moduleComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-lg text-center">
          {/* Check icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-6 shadow-lg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1 className="text-3xl font-serif font-bold text-slate-900 dark:text-slate-100 mb-3">
            Module Complete
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-2">
            {config.title}
          </p>
          <p className="text-slate-500 dark:text-slate-500 mb-8">
            Your reflections, assessments, and conversation transcripts have been saved.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {isExporting ? 'Downloading...' : 'Download Full Report (JSON)'}
            </button>

            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
            >
              Back to Home
            </button>
          </div>

          <p className="mt-8 text-xs text-slate-400 dark:text-slate-500">
            The JSON report contains your full interaction history — slides viewed, assessments,
            chat conversations, voice transcripts, and reflection summaries.
          </p>
        </div>
      </div>
    );
  }

  const currentPhase = state.currentPosition.phase;
  const currentPartIndex = state.currentPosition.partIndex;
  const partId = `part-${currentPartIndex + 1}`;

  return (
    <ModuleShell config={config} state={state} learnerId={state.id}>
      {currentPhase === 'content' && <SlideViewer moduleId={moduleId!} state={state} />}

      {currentPhase === 'assessment' && (
        <AssessmentPage
          moduleId={moduleId!}
          partId={partId}
          learnerId={state.id}
          onComplete={() => {
            // Move to reflection phase
            const { updatePosition } = useLearnerState.getState();
            updatePosition({
              ...state.currentPosition,
              phase: 'reflection',
            });
          }}
        />
      )}

      {currentPhase === 'companion' && <SlideViewer moduleId={moduleId!} state={state} />}

      {currentPhase === 'reflection' && (
        <>
          {/* Final Reflection (Part 3) */}
          {currentPartIndex === 2 ? (
            reflectionAgent ? (
              reflectionAgent.mode === 'voice' ? (
                /* Voice Reflection for Part 3 */
                <VoiceReflection
                  learnerId={state.id}
                  moduleId={moduleId!}
                  agentConfig={reflectionAgent}
                  learnerState={state}
                  onComplete={() => setModuleComplete(true)}
                />
              ) : (
                /* Text-based Final Reflection */
                <FinalReflection
                  learnerId={state.id}
                  moduleId={moduleId!}
                  learnerState={state}
                  onComplete={() => setModuleComplete(true)}
                />
              )
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-slate-600 dark:text-slate-400">Loading reflection...</p>
                </div>
              </div>
            )
          ) : (
            /* Regular Part Reflection (Parts 1 & 2) */
            reflectionAgent ? (
              <ReflectionPage
                learnerId={state.id}
                moduleId={moduleId!}
                partId={partId}
                agentConfig={reflectionAgent}
                onComplete={() => {
                  // Move to next part
                  const { updatePosition } = useLearnerState.getState();
                  updatePosition({
                    partIndex: currentPartIndex + 1,
                    phase: 'content',
                    slideIndex: 0,
                  });
                }}
              />
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-slate-600 dark:text-slate-400">Loading reflection...</p>
                </div>
              </div>
            )
          )}
        </>
      )}
    </ModuleShell>
  );
}
